import * as TWEEN from '@tweenjs/tween.js';
import { MeshStandardMaterial, Vector3, type Color } from 'three';

import ViewPort from '../ViewPort';
import { TWEEN_TIMES } from '../constants';
import type { PART_TYPES } from '../constants';
import { isClose, isCloseVectors } from '../geom-utils';
import type { Body } from '../loaders/body';
import type { StepFaceModel } from '../loaders/step/StepFaceModel';
import { handleUpdateHighlightView } from './paint-utils';
import type { Renderer } from './renderer';
import type { SnappableFeature } from './snapto-utils';

export type TweenStart = {
  start(): Promise<void>;
  cancel(): void;
};

const noopStart: TweenStart = {
  start() {
    return Promise.resolve();
  },
  cancel() {
    return;
  },
};

let activeViewportTween: TweenStart | null = null;
export const makeViewportTween = (
  renderer: Renderer,
  startViewport: ViewPort,
  finalViewport: ViewPort,
  time = TWEEN_TIMES.DEFAULT,
): TweenStart => {
  if (activeViewportTween) {
    activeViewportTween.cancel();
  }
  if (ViewPort.isCloseViewport(startViewport, finalViewport)) {
    return noopStart;
  }
  let resolver: (() => void) | null;
  let tween: TWEEN.Tween<ViewPort> | null;
  return (activeViewportTween = {
    start() {
      return new Promise<void>((resolve) => {
        resolver = resolve;
        const tweenViewport = startViewport.clone();
        renderer.requestRenderIfNotRequested();
        // make tweens for each camera side
        tween = new TWEEN.Tween(tweenViewport)
          .to(finalViewport, time)
          .easing(TWEEN.Easing.Linear.None)
          .onUpdate(async () => {
            tweenViewport.updateObjectProps(renderer.camera);
            renderer.state.onOrthoViewChange(tweenViewport.clone());
            renderer.camera.updateProjectionMatrix();
            const d1 = renderer.maxViewport.diagonal;
            const d2 = tweenViewport.diagonal;
            await renderer.state.onCameraZoomChange(d1 / d2);
            renderer.requestRenderIfNotRequested();
          })
          .onComplete(() => {
            renderer.trackball.enabled = true;
            renderer.canKeyInteract = true;
            renderer.requestRenderIfNotRequested();
            resolve();
            activeViewportTween = tween = resolver = null;
          })
          .start();
      });
    },
    cancel() {
      tween?.stop();
      if (resolver) {
        resolver();
      }
      activeViewportTween = tween = resolver = null;
      renderer.trackball.enabled = true;
      renderer.canKeyInteract = true;
      renderer.requestRenderIfNotRequested();
    },
  });
};

/*
Tweens the viewport of the renderer based on parameters. Performs a zoom to the checkpoint
and then the zoom to final viewport. Enacts callbacks at those times
*/
let activeOrthoCursorTween: TweenStart | null = null;
export const makeOrthoCursorTween = (
  renderer: Renderer,
  startViewport: ViewPort,
  finalViewport: ViewPort,
  checkpointViewport: ViewPort,
  midpointCallBack?: () => void | Promise<void>,
  finalCallback?: () => void | Promise<void>,
  outTime = TWEEN_TIMES.DEFAULT / 2,
  inTime = TWEEN_TIMES.DEFAULT / 2,
  force = false,
): TweenStart => {
  if (ViewPort.isCloseViewport(startViewport, finalViewport)) {
    if (force) {
      // eslint-disable-next-line no-param-reassign
      outTime = TWEEN_TIMES.FORCE;
      // eslint-disable-next-line no-param-reassign
      inTime = TWEEN_TIMES.FORCE;
    } else {
      return noopStart;
    }
  }
  if (activeOrthoCursorTween) {
    activeOrthoCursorTween.cancel();
  }
  let resolver: (() => void) | null;
  let zoomInTween: TWEEN.Tween<ViewPort> | null;
  let zoomOutTween: TWEEN.Tween<ViewPort> | null;
  return (activeOrthoCursorTween = {
    start() {
      return new Promise<void>((resolve) => {
        resolver = resolve;
        const tweenViewport = startViewport.clone();
        const onUpdate = async () => {
          tweenViewport.updateObjectProps(renderer.camera);
          renderer.state.onOrthoViewChange(tweenViewport.clone());
          renderer.camera.updateProjectionMatrix();
          const d1 = renderer.maxViewport.diagonal;
          const d2 = tweenViewport.diagonal;
          await renderer.state.onCameraZoomChange(d1 / d2);
          renderer.requestRenderIfNotRequested();
        };
        // make tweens for each camera side: Zoom out, then zoom In
        renderer.requestRenderIfNotRequested();
        zoomOutTween = new TWEEN.Tween(tweenViewport)
          .to(checkpointViewport, inTime)
          .easing(TWEEN.Easing.Linear.None)
          .onUpdate(onUpdate)
          .onComplete(async () => {
            zoomOutTween = null;
            if (midpointCallBack) await midpointCallBack();
            renderer.requestRenderIfNotRequested();
            zoomInTween = new TWEEN.Tween(tweenViewport)
              .to(finalViewport, outTime)
              .easing(TWEEN.Easing.Linear.None)
              .onUpdate(onUpdate)
              .onComplete(async () => {
                renderer.trackball.enabled = true;
                renderer.canKeyInteract = true;
                if (finalCallback) await finalCallback();
                renderer.requestRenderIfNotRequested();
                resolve();
                activeOrthoCursorTween =
                  zoomOutTween =
                  zoomInTween =
                  resolver =
                    null;
              })
              .start();
          })
          .start();
      });
    },
    cancel() {
      zoomOutTween?.stop();
      zoomInTween?.stop();
      if (resolver) {
        resolver();
      }
      activeOrthoCursorTween = zoomOutTween = zoomInTween = resolver = null;
      renderer.trackball.enabled = true;
      renderer.canKeyInteract = true;
      renderer.requestRenderIfNotRequested();
    },
  });
};

/*
Tweens the camera position with deflection about the camera final up to avoid jumpy gimbal
lock transitions.
*/
let activeCameraPositionTweenWithDeflectionTween: TweenStart | null = null;
export const makeCameraPositionTweenWithDeflection = (
  renderer: Renderer,
  finalPosition: Vector3,
  finalUp: Vector3,
  time = TWEEN_TIMES.DEFAULT,
): TweenStart => {
  // create a path that bulges around the final up
  // if up is paraller or antiparallel with across path, then use a default deflection vector
  // (if the default deflection looks better always just use that, or come up with another way to deflect)
  if (isCloseVectors(renderer.camera.position, finalPosition)) {
    return noopStart;
  }
  if (activeCameraPositionTweenWithDeflectionTween) {
    activeCameraPositionTweenWithDeflectionTween.cancel();
  }
  let resolver: (() => void) | null;
  let tween: TWEEN.Tween<Vector3> | null;
  return (activeCameraPositionTweenWithDeflectionTween = {
    start() {
      return new Promise<void>((resolve) => {
        resolver = resolve;
        const initialPosition = renderer.camera.position.clone();
        const separationVector = new Vector3().subVectors(
          finalPosition,
          initialPosition,
        );
        const sepLength = separationVector.length();
        separationVector.normalize();
        let deflectionAxis = new Vector3().crossVectors(
          finalUp,
          separationVector,
        );
        if (isClose(deflectionAxis.length(), 0)) {
          deflectionAxis = new Vector3(1, 1, 1);
        }
        deflectionAxis.normalize();

        const xArray: number[] = [];
        const yArray: number[] = [];
        const zArray: number[] = [];
        const maxDeflection = 0.25 * sepLength;
        for (let i = 0; i < 100; i++) {
          const fraction = i / 100;
          const pointAlong = new Vector3().addVectors(
            initialPosition,
            separationVector.clone().multiplyScalar(fraction * sepLength),
          );
          const deflectionFraction =
            -1 * (Math.abs(-2 * (fraction - 0.5)) - 1.0);
          const deflectedPoint = pointAlong.add(
            deflectionAxis
              .clone()
              .multiplyScalar(maxDeflection * deflectionFraction),
          );
          xArray.push(deflectedPoint.x);
          yArray.push(deflectedPoint.y);
          zArray.push(deflectedPoint.z);
        }
        xArray.push(finalPosition.x);
        yArray.push(finalPosition.y);
        zArray.push(finalPosition.z);

        renderer.requestRenderIfNotRequested(); //important must render before .start(), or it will never start
        tween = new TWEEN.Tween(renderer.camera.position)
          .to(
            {
              x: xArray,
              y: yArray,
              z: zArray,
            },
            time,
          )
          .interpolation(TWEEN.Interpolation.Linear)
          .onUpdate(() => {
            renderer.requestRenderIfNotRequested();
          })
          .onComplete(() => {
            renderer.trackball.enabled = true;
            renderer.canKeyInteract = true;
            renderer.requestRenderIfNotRequested();
            resolve();
            activeCameraPositionTweenWithDeflectionTween =
              tween =
              resolver =
                null;
          })
          .start();
      });
    },
    cancel() {
      tween?.stop();
      if (resolver) {
        resolver();
      }
      activeCameraPositionTweenWithDeflectionTween = tween = resolver = null;
      renderer.trackball.enabled = true;
      renderer.canKeyInteract = true;
      renderer.requestRenderIfNotRequested();
    },
  });
};

/*
Tweens the camera position from its current position to the provided one
*/
let activeCameraPositionTween: TweenStart | null = null;
export const makeCameraPositionTween = (
  renderer: Renderer,
  finalPosition: Vector3,
  time = TWEEN_TIMES.DEFAULT,
  freezeTrackball = false,
): TweenStart => {
  if (isCloseVectors(renderer.camera.position, finalPosition)) {
    return noopStart;
  }
  if (activeCameraPositionTween) {
    activeCameraPositionTween.cancel();
  }
  let resolver: (() => void) | null;
  let tween: TWEEN.Tween<Vector3> | null;
  return (activeCameraPositionTween = {
    start() {
      return new Promise<void>((resolve) => {
        resolver = resolve;
        renderer.requestRenderIfNotRequested();
        tween = new TWEEN.Tween(renderer.camera.position)
          .to(
            {
              x: finalPosition.x,
              y: finalPosition.y,
              z: finalPosition.z,
            },
            time,
          )
          .easing(TWEEN.Easing.Linear.None)
          .onUpdate(() => {
            renderer.requestRenderIfNotRequested();
          })
          .onComplete(() => {
            if (freezeTrackball) {
              renderer.trackball.enabled = true;
              renderer.canKeyInteract = true;
            }
            renderer.requestRenderIfNotRequested();
            resolve();
            activeCameraPositionTween = tween = resolver = null;
          })
          .start();
      });
    },
    cancel() {
      tween?.stop();
      if (resolver) {
        resolver();
      }
      activeCameraPositionTween = tween = resolver = null;
      if (freezeTrackball) {
        renderer.trackball.enabled = true;
        renderer.canKeyInteract = true;
      }
      renderer.requestRenderIfNotRequested();
    },
  });
};

let activeControlTargetTween: TweenStart | null = null;
export const makeControlTargetTween = (
  renderer: Renderer,
  finalTarget: Vector3,
  time = TWEEN_TIMES.DEFAULT,
): TweenStart => {
  if (isCloseVectors(renderer.trackball.target, finalTarget)) {
    return noopStart;
  }
  if (activeControlTargetTween) {
    activeControlTargetTween.cancel();
  }
  let resolver: (() => void) | null;
  let tween: TWEEN.Tween<Vector3> | null;
  return (activeControlTargetTween = {
    start() {
      return new Promise<void>((resolve) => {
        resolver = resolve;
        renderer.requestRenderIfNotRequested();
        tween = new TWEEN.Tween(renderer.trackball.target)
          .to(
            {
              x: finalTarget.x,
              y: finalTarget.y,
              z: finalTarget.z,
            },
            time,
          )
          .easing(TWEEN.Easing.Linear.None)
          .onUpdate(() => {
            renderer.requestRenderIfNotRequested();
          })
          .onComplete(() => {
            renderer.trackball.enabled = true;
            renderer.canKeyInteract = true;
            renderer.requestRenderIfNotRequested();
            resolve();
            activeControlTargetTween = tween = resolver = null;
          })
          .start();
      });
    },
    cancel() {
      tween?.stop();
      if (resolver) {
        resolver();
      }
      activeControlTargetTween = tween = resolver = null;
      renderer.trackball.enabled = true;
      renderer.canKeyInteract = true;
      renderer.requestRenderIfNotRequested();
    },
  });
};

let activeUpTween: TweenStart | null = null;
export const makeUpTween = (
  renderer: Renderer,
  finalUp: Vector3,
  time = TWEEN_TIMES.DEFAULT,
): TweenStart => {
  // need a tween to restore the default up state of the camera to ensure angles match on selections
  if (isCloseVectors(renderer.camera.up, finalUp)) {
    return noopStart;
  }
  if (activeUpTween) {
    activeUpTween.cancel();
  }
  let resolver: (() => void) | null;
  let tween: TWEEN.Tween<Vector3> | null;
  return (activeUpTween = {
    start() {
      return new Promise<void>((resolve) => {
        resolver = resolve;
        renderer.requestRenderIfNotRequested();
        tween = new TWEEN.Tween(renderer.camera.up)
          .to(
            {
              x: finalUp.x,
              y: finalUp.y,
              z: finalUp.z,
            },
            time,
          )
          .easing(TWEEN.Easing.Linear.None)
          .onUpdate(() => {
            renderer.requestRenderIfNotRequested();
          })
          .onComplete(() => {
            renderer.requestRenderIfNotRequested();
            resolve();
            activeUpTween = tween = resolver = null;
          })
          .start();
      });
    },
    cancel() {
      tween?.stop();
      if (resolver) {
        resolver();
      }
      activeUpTween = tween = resolver = null;
      renderer.trackball.enabled = true;
      renderer.canKeyInteract = true;
      renderer.requestRenderIfNotRequested();
    },
  });
};

let activeColorTween: TweenStart | null = null;
export const makeColorTween = (
  renderer: Renderer,
  featureInfo: SnappableFeature,
  outTime = TWEEN_TIMES.DEFAULT / 2,
  inTime = TWEEN_TIMES.DEFAULT / 2,
): TweenStart => {
  if (activeColorTween) {
    activeColorTween.cancel();
  }
  let resolver: (() => void) | null;
  let zoomOutTween: TWEEN.Tween<{
    opacity: number;
    emissiveIntensity: number;
  }> | null;
  let zoomInTween: TWEEN.Tween<{
    opacity: number;
    emissiveIntensity: number;
  }> | null;
  return (activeColorTween = {
    start() {
      return new Promise<void>((resolve) => {
        resolver = resolve;
        const { faces: faceIndices, color, referenceUuid } = featureInfo;
        const { bodyOrAssemblyInstanceById, bodyIdsByPartFileUuid } =
          renderer.state;

        const facesToMute: StepFaceModel[] = [];
        const facesToHighlight: StepFaceModel[] = [];

        for (const bodyId of bodyIdsByPartFileUuid.get(referenceUuid) || []) {
          const body = bodyOrAssemblyInstanceById.get(
            bodyId,
          ) as Body<PART_TYPES.STEP>;
          if (!body.isLoaded) continue;
          if (faceIndices && body.obj.faces) {
            for (const face of body.obj.faces.values()) {
              if (!faceIndices.includes(face.index)) {
                facesToMute.push(face);
              } else {
                facesToHighlight.push(face);
              }
            }
          }
        }

        // TODO: do edges as well for active coloring

        const tweenFacesToMute = facesToMute;
        const tweenFacesToHighlight = facesToHighlight;
        const tweenMatProps = {
          opacity: 1,
          emissiveIntensity: 0,
        };
        const tweenEmissiveColor = color;
        renderer.requestRenderIfNotRequested();

        zoomOutTween = new TWEEN.Tween(tweenMatProps)
          .to(
            {
              opacity: 0.0,
              emissiveIntensity: 1.0,
            },
            outTime,
          )
          .easing(TWEEN.Easing.Exponential.Out)
          .onUpdate(() => {
            //TODO: Review
            for (const face of tweenFacesToMute) {
              face.setOpacity(tweenMatProps.opacity as number);
            }
            for (const face of tweenFacesToHighlight) {
              face.emissive.setHex(tweenEmissiveColor as number);
              face.emissiveIntensity =
                tweenMatProps.emissiveIntensity as number;
            }
          })
          .onComplete(() => {
            renderer.requestRenderIfNotRequested();
            zoomOutTween = null;
            zoomInTween = new TWEEN.Tween(tweenMatProps)
              .to(
                {
                  opacity: 1.0,
                  emissiveIntensity: 0.0,
                },
                inTime,
              )
              .easing(TWEEN.Easing.Exponential.In)
              .onUpdate(() => {
                //TODO: Review
                for (const face of tweenFacesToMute) {
                  face.setOpacity(tweenMatProps.opacity as number);
                }
                for (const face of tweenFacesToHighlight) {
                  face.emissive.setHex(tweenEmissiveColor as number);
                  face.emissiveIntensity =
                    tweenMatProps.emissiveIntensity as number;
                }
                renderer.requestRenderIfNotRequested();
              })
              .onComplete(() => {
                handleUpdateHighlightView(renderer.state);
                renderer.trackball.enabled = true;
                renderer.canKeyInteract = true;
                renderer.requestRenderIfNotRequested();
                resolve();
                activeColorTween = zoomInTween = zoomOutTween = resolver = null;
              })
              .start();
          })
          .start();
      });
    },
    cancel() {
      zoomOutTween?.stop();
      zoomInTween?.stop();
      if (resolver) {
        resolver();
      }
      activeColorTween = zoomInTween = zoomOutTween = resolver = null;
      handleUpdateHighlightView(renderer.state);
      renderer.trackball.enabled = true;
      renderer.canKeyInteract = true;
      renderer.requestRenderIfNotRequested();
    },
  });
};

/*
Tweens the opacity of a material (used in cube hover)
*/
export const makeMaterialOpacityTween = (
  renderer: Renderer,
  material: MeshStandardMaterial,
  opacity: number,
  time = TWEEN_TIMES.CUBE_COLOR,
): Promise<void> => {
  return new Promise((resolve) => {
    renderer.requestRenderIfNotRequested();
    new TWEEN.Tween(material)
      .to({ opacity }, time)
      .onUpdate((m: MeshStandardMaterial) => {
        //When opacity is 0, turn off depthWrite
        //When there is some opacity (remember, it's transparent), then depthWrite should be true
        m.depthWrite = opacity !== 0;
        renderer.requestRenderIfNotRequested();
      })
      .onComplete((m: MeshStandardMaterial) => {
        //When opacity is 0, turn off depthWrite
        //When there is some opacity (remember, it's transparent), then depthWrite should be true
        m.depthWrite = opacity !== 0;
        renderer.requestRenderIfNotRequested();
        resolve();
      })
      .start();
    renderer.requestRenderIfNotRequested();
  });
};

/*
Tweens the color of a material (used in cube click)
*/
let activeMaterialColorTween: TweenStart | null = null;
export const makeMaterialColorTween = (
  renderer: Renderer,
  material: MeshStandardMaterial,
  color: { r: number; g: number; b: number },
  onComplete?: () => void | Promise<void>,
  time = TWEEN_TIMES.CUBE_COLOR,
): TweenStart => {
  if (activeMaterialColorTween) {
    activeMaterialColorTween.cancel();
  }
  let resolver: (() => void) | null;
  let tween: TWEEN.Tween<Color> | null;
  return (activeMaterialColorTween = {
    start() {
      return new Promise<void>((resolve) => {
        resolver = resolve;
        renderer.requestRenderIfNotRequested();
        tween = new TWEEN.Tween(material.color)
          .to(color, time)
          .onStart(() => {
            renderer.requestRenderIfNotRequested();
          })
          .onUpdate(function () {
            renderer.requestRenderIfNotRequested();
          })
          .onComplete(async () => {
            if (onComplete) {
              await onComplete();
            } else {
              renderer.trackball.enabled = true;
              renderer.canKeyInteract = true;
            }
            renderer.requestRenderIfNotRequested();
            activeMaterialColorTween = tween = resolver = null;
            resolve();
          })
          .start();
      });
    },
    cancel() {
      tween?.stop();
      if (resolver) {
        resolver();
      }
      activeMaterialColorTween = tween = resolver = null;
      handleUpdateHighlightView(renderer.state);
      renderer.trackball.enabled = true;
      renderer.canKeyInteract = true;
      renderer.requestRenderIfNotRequested();
    },
  });
};
